import CheckIcon from '@mui/icons-material/Check';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { useCallback } from 'react';
import { useShallow } from 'zustand/shallow';
import { useFilterEvents } from '@repo/ui/OffersFilter/useFilterEvents';
import { useOffersFilterStore } from '../store';
import { useRangeFilterOptions } from '../store/hooks';
import type { OffersFilterStoreState } from '../store/store';

export const MobileSelectTermLength = ({
  actionSection,
  pageIdentifier,
}: {
  actionSection: string;
  pageIdentifier: string;
}) => {
  const termLengthOptions = useRangeFilterOptions('cdDepositTerm');

  const { handleUpdateFilterFormState, handleApplyFilters, appliedFilters } = useOffersFilterStore(
    useShallow((state: OffersFilterStoreState) => ({
      handleUpdateFilterFormState: state.handleUpdateFilterFormState,
      handleApplyFilters: state.handleApplyFilters,
      appliedFilters: state.appliedFilters,
    })),
  );

  const { updateFiltersEvent } = useFilterEvents({
    actionSection,
    pageIdentifier,
    additionalAttributes: {},
  });

  const handleFieldLabelSelection = (selectedLabels: string[]) => {
    setTimeout(() => {
      handleUpdateFilterFormState({
        selectedTermRanges: selectedLabels,
      });

      const newState = handleApplyFilters();
      if (!newState) return;

      const additionalAttributes = {
        term_length: selectedLabels,
      };

      updateFiltersEvent({
        additional_attributes: additionalAttributes,
      });
    });
  };

  const selectedOptions = appliedFilters?.selectedTermRanges ?? [];

  const updateSelection = useCallback(
    (option: string) => {
      const isSelected = selectedOptions.includes(option);
      const newSelected = isSelected
        ? selectedOptions.filter(item => item !== option)
        : [...selectedOptions, option];

      handleFieldLabelSelection(newSelected);
    },
    [selectedOptions],
  );

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      sx={{
        border: 0,
        '&.Mui-expanded': {
          borderBottom: 0,
        },
        '&.MuiAccordion-root': {
          borderBottom: 0,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '&:hover': {
            backgroundColor: 'transparent',
          },
          borderBottom: '1px solid',
          borderBottomColor: 'neutral.lighter',
          px: 3,
          my: 0.5,
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: '#0069B9',
          },
          '&:focus:hover': {
            outline: '2px solid !important', // overriding default MUI CSS that has !important
            outlineColor: '#0069B9 !important', // overriding default MUI CSS that has !important
          },
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            color: 'text.secondary',
            fontWeight: 600,
          }}
        >
          TERM LENGTH
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        <List disablePadding>
          {Object.values(termLengthOptions).map(({ label, count }, index) => {
            const isDisabled = !count && !selectedOptions.includes(label);
            return (
              <ListItemButton
                key={`${index}-${label}`}
                sx={{
                  px: 3,
                  '&.Mui-selected': {
                    backgroundColor: 'transparent',
                    ':hover': { backgroundColor: 'neutral.lightest' },
                    ':focus-visible': { backgroundColor: 'neutral.lightest' },
                  },
                }}
                disabled={isDisabled}
                onClick={() => updateSelection(label)}
              >
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        '&:focus-visible': {
                          outline: '2px solid',
                          outlineColor: 'blues.light',
                          outlineOffset: '0',
                        },
                      }}
                    >
                      <Typography
                        variant="textSmall"
                        sx={{ color: 'neutral.dark', fontSize: '14px' }}
                      >
                        {label}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography
                          variant="textSmall"
                          sx={{ color: 'neutral.dark', fontSize: '12px' }}
                        >
                          {count}
                        </Typography>
                        <Checkbox
                          tabIndex={-1}
                          checked={selectedOptions.includes(label)}
                          checkedIcon={
                            <span
                              style={{
                                display: 'inline-block',
                                width: 24,
                                height: 24,
                                position: 'relative',
                              }}
                            >
                              <CheckBoxOutlineBlankIcon
                                sx={{
                                  color: 'primary.main',
                                  fontSize: 24,
                                }}
                              />
                              <CheckIcon
                                sx={{
                                  color: 'primary.main',
                                  fontSize: 18,
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                }}
                              />
                            </span>
                          }
                          sx={{
                            p: 0,
                            '&:not(.Mui-checked) .MuiSvgIcon-root': {
                              fontSize: 24,
                              color: 'neutral.lighter',
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  }
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: 14,
                    },
                  }}
                ></ListItemText>
              </ListItemButton>
            );
          })}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};
